import dayjs from 'dayjs';

import { selectIsAuthenticated } from '@/domain/auth/auth.selectors';
import { CalculateVenueEnv } from '@/domain/impact-calculate-venue/entities/calculate-venue-env.entity';
import { selectImpactCalculateVenueEnvForProject } from '@/domain/impact-calculate-venue/impact-calculate-venue.selectors';
import { ProjectVenue } from '@/domain/project-venue/entities/project-venue.entity';
import { selectCurrentProjectVenue } from '@/domain/project-venue/project-venue.selectors';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';

import { extendVenueEnvCalculate } from '../../pdf/utils/compute-extend-env';
import {
  displayDecimal,
  formatTopicScore,
} from '../../pdf/utils/display-scores.utils';
import ArrowIcon from '../assets/arrow.svg';
import CrossImg from '../assets/cross.svg';
import FairlyScorePillLogo from '../assets/fairly-score-pill-logo.svg';
import { MainScorePill } from '../components/MainScorePill';

export function ScoreCardsVenueC3() {
  document.documentElement.classList.add('score-card');
  document.body.classList.add('score-card');
  dayjs.locale('fr');

  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const venue = useAppSelector(
    selectCurrentProjectVenue,
  ) satisfies ProjectVenue | null;
  const venueId = venue?.id ?? 0;

  const impactEnv = extendVenueEnvCalculate(
    useAppSelector(selectImpactCalculateVenueEnvForProject(venueId)),
  ) satisfies CalculateVenueEnv;

  const impactEnvScore100 =
    !impactEnv.global.score || !impactEnv.global.maxScore
      ? 0
      : Math.round((impactEnv.global.score * 100) / impactEnv.global.maxScore);

  if (!isAuthenticated) {
    return <h1>{translate('PdfExports.user-not-authentified')}</h1>;
  }

  if (!venue) {
    return <h1>{translate('PdfExports.venue-not-found')}</h1>;
  }

  return (
    <div id="card3" className="score-card-section">
      <div className="title">
        <img
          src={FairlyScorePillLogo}
          width="80"
          height="80"
          alt="Fairly logo"
        />
        <img src={CrossImg} width="30" height="30" />
        <h1>{venue.name}</h1>
      </div>
      <div className="scores">
        <div className="subtitle">
          <div className="score-row-left">
            <img src={ArrowIcon} width="40" height="40" />
            <span>{translate('Projects.env')}</span>
          </div>
          <MainScorePill
            scoreLetter={impactEnv.global.scoreLetter}
            type="env"
          />
        </div>
        <div className="score-row highlight">
          <span>{translate('PdfExports.total-score')}</span>
          <span className="highlight">
            {displayDecimal(impactEnvScore100)} / {100}
          </span>
        </div>
        <div className="score-row">
          <span>{translate('Env.Topics.FOOD_BEVERAGES')}</span>
          <span>
            {formatTopicScore(impactEnv.structure.topics.FOOD_BEVERAGES)}
          </span>
        </div>
        <div className="score-row">
          <span>{translate('Env.Topics.RESOURCES_POLLUTION')}</span>
          <span>
            {formatTopicScore(impactEnv.structure.topics.RESOURCES_POLLUTION)}
          </span>
        </div>
        <div className="score-row">
          <span>{translate('Env.Topics.COMMUNICATION')}</span>
          <span>
            {formatTopicScore(impactEnv.structure.topics.COMMUNICATION)}
          </span>
        </div>
        <div className="score-row">
          <span>{translate('Env.Topics.PUBLIC_ENGAGEMENT')}</span>
          <span>
            {formatTopicScore(impactEnv.structure.topics.PUBLIC_ENGAGEMENT)}
          </span>
        </div>
      </div>

      <div className="edition-date">
        {translate('PdfExports.edition-date', { date: dayjs().format('LL') })}
      </div>
    </div>
  );
}
